import React from 'react'
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space, Tooltip } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderStatus } from '../../theme/columns'
import { useRowShow, ListPage, ProgressBar, TableSurface, type Stat } from '../../theme/listkit'
import { HandHeart, Target, Rocket, BarChart3 } from '../../components/icons'

type Campaign = Tables<'campaigns'>
// The admin summary view's `id` column is nullable in the generated types, which
// conflicts with refine's BaseRecord; declare just the deck columns we read.
type CampaignsSummary = {
  active_count: number | null
  total_raised: number | null
  total_goal: number | null
}

/** The exact ledger format the table cells use ($X,XXX.XX) — preserved verbatim. */
const fmtCents = (value: number) =>
  `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** Compact money for the stat deck (distinct strings from the cells). */
const compact = (n: number) =>
  n >= 1000 ? '$' + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K' : '$' + Math.round(n).toLocaleString()

export function CampaignList() {
  const { tableProps } = useTable<Campaign>({
    syncWithLocation: true,
  })
  const onRow = useRowShow('campaigns')

  // Stat deck reads ONE pre-aggregated row from the admin summary view instead
  // of fetching the whole campaigns table and reducing on the main thread.
  const { result: summaryRes } = useList<CampaignsSummary>({
    resource: 'admin_campaigns_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]
  const active = Number(summary?.active_count ?? 0)
  const totalRaised = Number(summary?.total_raised ?? 0)
  const totalGoal = Number(summary?.total_goal ?? 0)
  const pctToGoal = totalGoal > 0 ? Math.round((totalRaised / totalGoal) * 100) : 0

  const stats: Stat[] = [
    { label: 'Active Campaigns', value: active.toLocaleString(), icon: Rocket, tone: 'jaguar' },
    { label: 'Total Raised', value: compact(totalRaised), icon: HandHeart, tone: 'gold' },
    { label: 'Total Goal', value: compact(totalGoal), icon: Target, tone: 'gold' },
    { label: '% to Goal', value: `${pctToGoal}%`, hint: 'all campaigns', icon: BarChart3, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Giving · Campaigns',
        description:
          'Time-boxed fundraising drives — the Annual Fund, scholarship pushes, capital campaigns. Watch each climb toward its goal and reconcile what every drive has raised.',
      }}
      stats={stats}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Campaign>
              dataIndex="title"
              title="Title"
              render={(title: string) => (
                <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 14, color: brand.ink }}>
                  {title}
                </span>
              )}
            />
            <Table.Column<Campaign>
              dataIndex="raised"
              title="Raised"
              sorter
              render={(value: number) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 700, color: brand.gold }}>{fmtCents(value)}</span>
              )}
            />
            <Table.Column<Campaign>
              dataIndex="goal"
              title="Goal"
              render={(value: number) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 700, color: brand.ink }}>{fmtCents(value)}</span>
              )}
            />
            <Table.Column<Campaign>
              title="Progress"
              dataIndex="progress"
              width={180}
              render={(_: unknown, record: Campaign) => {
                const raised = Number(record.raised || 0)
                const goal = Number(record.goal || 0)
                const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0
                return (
                  <div style={{ minWidth: 150 }}>
                    <ProgressBar value={raised} max={goal || 1} height={8} />
                    <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 11, color: brand.sub, marginTop: 5 }}>
                      {pct}% to goal
                    </div>
                  </div>
                )
              }}
            />
            <Table.Column<Campaign> dataIndex="donors" title="Donors" render={(value: number) => String(value)} />
            <Table.Column<Campaign>
              dataIndex="days_remaining"
              title="Days Remaining"
              render={(value: number | null) => (value != null ? String(value) : '—')}
            />
            <Table.Column<Campaign>
              dataIndex="is_active"
              title="Active"
              render={(value: boolean) => renderStatus(value ? 'Active' : 'Inactive')}
            />
            <Table.Column<Campaign>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Campaign) => {
                // A campaign that has donations CANNOT be deleted: donations.campaign_id
                // is ON DELETE NO ACTION, so Postgres refuses (23503). That guard is
                // deliberate — removing a drive must never remove giving history. The
                // button stays enabled (the database is the authority, and the
                // denormalised `donors` counter could drift), but we warn up front and
                // translate the raw FK error into something an admin can act on.
                const hasGiving = Number(record.donors || 0) > 0
                return (
                  <Space onClick={(e) => e.stopPropagation()}>
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <Tooltip
                      title={
                        hasGiving
                          ? 'This campaign has donations — giving history is never deleted. Mark it inactive instead.'
                          : 'Delete this campaign'
                      }
                    >
                      <DeleteButton
                        hideText
                        size="small"
                        recordItemId={record.id}
                        data-testid={`campaign-delete-${record.id}`}
                        confirmTitle={`Delete “${record.title}”?`}
                        confirmOkText="Delete"
                        errorNotification={(error) => {
                          const msg =
                            (error as { message?: string } | undefined)?.message ?? ''
                          if (/foreign key|23503|still referenced|violates/i.test(msg)) {
                            return {
                              type: 'error',
                              message: 'This campaign has donations',
                              description:
                                'Giving history is never deleted, so a campaign with donations cannot be removed. Uncheck "Active" on the campaign instead — that hides it from members while keeping the ledger intact.',
                            }
                          }
                          return {
                            type: 'error',
                            message: 'Could not delete campaign',
                            description: msg || 'Please try again.',
                          }
                        }}
                        successNotification={{
                          type: 'success',
                          message: 'Campaign deleted',
                        }}
                      />
                    </Tooltip>
                  </Space>
                )
              }}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
